/*
 * This file is part of the Norges Musikkhøyskole Nettsted 2020 application.
 *
 * Test
 *
 * (c) APT AS
 *
 * For the full copyright and license information, please view the LICENSE
 * file that was distributed with this source code.
 */

import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { DateUtils } from 'react-day-picker';
import { useDebouncedCallback } from 'use-debounce';
import axios from 'axios';
import isEqual from 'lodash/isEqual';
import { useMediaQuery } from 'react-responsive';
import shortid from 'shortid';

import useKeys from 'effects/keys';
import usePrevious from 'hooks/usePrevious';
import useQueryString from './hooks/useQueryString';
import OptionsType from './OptionsType/OptionsType';
import Result from './Result/Result';
import DateType from './DateType/DateType';
import ExpandableText from 'components/ExpandableText/ExpandableText';

import IconQuestionMark from 'gfx/icons/question-mark.svg';
import IconFilters from 'gfx/icons/filters.svg';

import styles from './Filter.module.scss';

const ERROR_REQUEST_ID_MISMATCH = 'ERROR_REQUEST_ID_MISMATCH';
const REQUEST_DEBOUNCE_TIME = 50;
const REQUEST_MAX_WAIT = 500;

/**
 * This is the Filter component.
 *
 * @author Anders Stalheim Øfsdahl <anders@apt.no>
 *
 * @return {JSX}
 */
function Filter({
  content,
  resultPortal,
  resultView,
  endpoint,
  filters: initialFilters,
  query: initialQuery,
  parent,
  allowSearch,
  hideFilters,
  labels,
  locale,
  loadOnInit,
  itemWidth,
  defaultSettings,
  csrf,
}) {
  const [filters] = useState([
    ...initialFilters.filter(
      (f) =>
        f.type !== 'options' || (Array.isArray(f.items) && f.items.length > 0)
    ),
    {
      label: 'Tags',
      name: 'tags',
      type: 'options',
      static: true,
    },
  ]);

  const [showFilters, setShowFilters] = useState(false); //  Use by small layout to toggle visibility of filters
  const [state, setState] = useQueryString({
    store: 'hash',
    initialFilters:
      defaultSettings && defaultSettings.filters ? defaultSettings.filters : {},
  });
  const [query, setQuery] = useState(state.query);
  const [open, setOpen] = useState(null);
  const prevState = usePrevious(state);
  const [result, setResult] = useState({ loading: false, data: null });
  const [page, setPage] = useState(1); // Current pagination page
  const [searchId] = useState(shortid.generate());
  const prevPage = usePrevious(page);
  const requestId = useRef(null);
  const cancelRef = useRef(null);
  const useLargeLayout = useMediaQuery({
    query: `(min-width: ${styles['page-width-tablet']})`,
  });

  useKeys('filter', !!open && useLargeLayout, {
    esc: () => {
      setOpen(null);
    },
  });

  const doApiCall = (state) => {
    // Cancel ongoing request!
    if (cancelRef.current) {
      cancelRef.current.cancel('Operation cancelled by user');
    }

    cancelRef.current = axios.CancelToken.source();

    // Generate a unique request ID
    requestId.current = shortid.generate();
    setResult({
      ...result,
      loading: true,
    });

    // Destructure state to split query into separate variable
    const { query, ...filters } = state;

    // Merge default filters with user filters
    let payload = {
      ...filters,
      query,
      parent,
      content,
      requestId: requestId.current,
      page,
      paginate: useLargeLayout ? 'desktop' : 'mobile',
      [csrf.tokenName]: csrf.token,
    };

    // Send POST request
    axios
      .post(endpoint, payload, {
        cancelToken: cancelRef.current.token,
      })
      .then((response) => {
        // Clear cancelToken ref
        cancelRef.current = null;
        return response;
      })
      .then((response) => {
        const data = response.data;
        if (data.requestId !== requestId.current) {
          // Returned data does not match current requestId, ignore returned data
          throw new Error({
            type: ERROR_REQUEST_ID_MISMATCH,
          });
        }
        return data;
      })
      .then((data) => {
        setResult({
          loading: false,
          data,
        });
      })
      .catch((error) => {
        if (error && error.type && error.type === ERROR_REQUEST_ID_MISMATCH) {
          console.warn('requestId mismatch, ignore returned results');
        } else {
          console.warn('error', error);
          setResult({
            loading: false,
            data: null,
          });
        }
      });
  };

  const debouncedApiCall = useDebouncedCallback(
    (value) => {
      doApiCall(value);
    },
    REQUEST_DEBOUNCE_TIME,
    { maxWait: REQUEST_MAX_WAIT }
  );

  const toggleFilters = () => {
    setShowFilters(!showFilters);
  };

  const toggleFilter = (filter) => {
    setOpen(filter === open ? null : filter);
  };

  const toggleOption = (filter, value) => {
    // Uncomment to disallow hidden filters to be closable
    // const filterOption = filters.find((f) => f.name === filter);
    // if (
    //   typeof filterOption.hidden === 'undefined' ||
    //   filterOption.hidden !== true
    // ) {
    setState(filter, value, 'toggle');
    // }
  };

  const setDateFilter = (filter, range) => {
    const now = new Date();
    const offset = now.getTimezoneOffset();

    // Join ranges to array, while formating to ISO string respecting time zone offset of user
    const temp = [];
    if (range.from) {
      const t = new Date(range.from.getTime() + offset * 60 * 1000);
      temp.push(t.toISOString().split('T')[0]);
    }
    if (range.to) {
      const t = new Date(range.to.getTime() + offset * 60 * 1000);
      temp.push(t.toISOString().split('T')[0]);
    }

    // Set updated state of date filter, joining date values into string
    setState(filter, temp.sort().join('_'));
  };

  const handlePredfinedRangeClick = (filter, type) => {
    const now = new Date();
    switch (type) {
      case 'today':
        setDateFilter(filter, {
          from: now,
        });
        break;

      case 'weekend':
        {
          const nextFriday = new Date();
          const diff = (7 + 5 - now.getDay()) % 7;
          nextFriday.setDate(now.getDate() + (diff !== 0 ? diff : 7));
          const nextSunday = new Date(nextFriday.getTime());
          nextSunday.setDate(nextSunday.getDate() + 2);
          setDateFilter(filter, {
            from: nextFriday,
            to: nextSunday,
          });
        }
        break;

      case 'month':
        {
          var firstDay = new Date(
            now.getFullYear(),
            now.getMonth(),
            1,
            12,
            0,
            0
          );
          var lastDay = new Date(
            now.getFullYear(),
            now.getMonth() + 1,
            0,
            12,
            0,
            0
          );
          setDateFilter(filter, {
            from: firstDay,
            to: lastDay,
          });
        }
        break;

      case 'upcoming':
        {
          var nextYear = new Date();
          nextYear.setFullYear(nextYear.getFullYear() + 1);
          setDateFilter(filter, {
            from: now,
            to: nextYear,
          });
        }
        break;

      default:
        console.warn(`Unable to handle range "${type}"`);
    }
  };

  const handleDayClick = (filter, day) => {
    // Split current date value into from/to parts
    const [from, to] = state[filter] ? state[filter].split('_') : [null, null];

    // Create range object based on current from/to values (or null)
    let currentRange = {
      from: from ? new Date(`${from}T12:00`) : null,
      to: to ? new Date(`${to}T12:00`) : null,
    };
    // Add clicked date to day range
    const nextRange = DateUtils.addDayToRange(day, currentRange);

    const isoFrom = nextRange.from
      ? nextRange.from.toISOString().split('T')[0]
      : 0;
    const isoTo = nextRange.to ? nextRange.to.toISOString().split('T')[0] : 0;

    if (!to && isoFrom === isoTo) {
      // Reset range if clicked to equals from, when there is no to already
      nextRange.from = null;
      nextRange.to = null;
    } else if (to && isoFrom == isoTo) {
      // Clear to if from/to is set, and to is set to same date as from
      nextRange.to = null;
    }

    setDateFilter(filter, nextRange);
  };

  const handleSubmitQuery = (e) => {
    e.preventDefault();
    const tagMatch = query.match(/(tag( |:))( *)([^\s].*)/i);
    if (tagMatch) {
      // If the inputed string matches the pattern, add as a tag instead of as query
      const tag = tagMatch[4].trim();
      setState('tags', tag, 'toggle');
      setQuery('');
    } else {
      setState('query', query && query.length > 1 ? query : '');
    }
  };

  const clearQuery = () => {
    setQuery('');
    setState('query', '');
  };

  // Renders list of active filters as button, to remove filter
  const renderActiveFilters = () => {
    const keys = Object.keys(state);

    if (keys.length < 1) {
      return null;
    }

    return (
      <div className={styles.active}>
        <span>{labels.active}</span>
        {keys.map((filterKey) => {
          const filter = filters.find((f) => f.name === filterKey);

          if (!filter) {
            if (filterKey === 'query') {
              return (
                <button
                  key={filterKey}
                  className="chip chip-active"
                  onClick={clearQuery}
                >
                  {state[filterKey]}
                </button>
              );
            }
          } else {
            if (filter.type === 'date') {
              const [from, to] = state[filterKey]
                ? state[filterKey].split('_')
                : [null, null];

              let currentRange = {
                from: from ? new Date(`${from}T12:00`) : null,
                to: to ? new Date(`${to}T12:00`) : null,
              };

              const lang = document.querySelector('html').getAttribute('lang');

              let text = `${currentRange.from.getDate()}. ${currentRange.from.toLocaleString(
                lang,
                { month: 'long' }
              )}`;

              if (to) {
                text = `${currentRange.from.getDate()}. ${currentRange.from.toLocaleString(
                  lang,
                  { month: 'long' }
                )} -
                  ${currentRange.to.getDate()}. ${currentRange.to.toLocaleString(
                  lang,
                  { month: 'long' }
                )}`;

                if (
                  currentRange.from.getFullYear() !==
                  currentRange.to.getFullYear()
                ) {
                  text = `${currentRange.from.getDate()}. ${currentRange.from.toLocaleString(
                    lang,
                    {
                      month: 'long',
                      year: 'numeric',
                    }
                  )} -
                    ${currentRange.to.getDate()}. ${currentRange.to.toLocaleString(
                    lang,
                    {
                      month: 'long',
                      year: 'numeric',
                    }
                  )}`;
                }
              }

              return (
                <button
                  key={filterKey}
                  className="chip chip-active"
                  onClick={() => setState(filterKey, null)}
                >
                  {text}
                </button>
              );
            } else if (filter.type === 'options') {
              if (filter.static) {
                const tagLabel = filter.name === 'tags' ? `Tag: ` : '';
                return state[filterKey].map((f) => (
                  <button
                    className="chip chip-active"
                    key={`${filter.name}_${f}`}
                    onClick={() => {
                      toggleOption(filter.name, f);
                    }}
                  >
                    {tagLabel}
                    {f}
                  </button>
                ));
              } else {
                return filter.items.map((option) => {
                  if (!state[filterKey].includes(option.value)) {
                    return null;
                  }
                  return (
                    <button
                      className="chip chip-active"
                      key={`${filter.name}_${option.value}`}
                      onClick={() => {
                        toggleOption(filter.name, option.value);
                      }}
                    >
                      {option.name}
                    </button>
                  );
                });
              }
            }
          }
        })}
      </div>
    );
  };

  const renderSearchField = () => {
    if (!allowSearch) {
      return null;
    }

    return (
      <div className={styles.search}>
        <form
          onSubmit={handleSubmitQuery}
          role="search"
          aria-label={labels.formLabel}
        >
          <label htmlFor={searchId} aria-label={labels.placeholder}>
            <IconQuestionMark />
          </label>
          <input
            id={searchId}
            type="text"
            placeholder={labels.placeholder}
            value={query ? query : ''}
            onChange={(e) => setQuery(e.target.value)}
          />
        </form>
      </div>
    );
  };

  // Renders layout
  const renderSmallLayout = () => {
    if (hideFilters) {
      return null;
    }

    return (
      <div className={classNames(styles.main)}>
        <div className={styles.wrapper}>
          <button className={styles.toggleFilters} onClick={toggleFilters}>
            <IconFilters />
            {showFilters ? labels.btnHideFilters : labels.btnShowFilters}
          </button>
        </div>
        <ExpandableText
          expanded={showFilters}
          calcMargins={true}
          shouldFadeIn={true}
        >
          <div className={styles.filters}>
            {filters
              .filter((filter) => !filter.static && !filter.hidden)
              .map((filter) => (
                <div key={filter.name}>
                  <button
                    className={classNames(styles.filter, {
                      [styles.filterOpen]: open === filter.name,
                    })}
                    onClick={() => {
                      toggleFilter(filter.name);
                    }}
                  >
                    {filter.label}
                    <span className="plus-icon"></span>
                  </button>
                  <ExpandableText
                    className={styles.options}
                    expanded={open === filter.name}
                    calcMargins={filter.type === 'date'}
                    shouldFadeIn={true}
                  >
                    {filter.type === 'options' && (
                      <OptionsType
                        items={filter.items}
                        active={state[filter.name]}
                        onClick={(value) => toggleOption(filter.name, value)}
                      />
                    )}
                    {filter.type === 'date' && (
                      <DateType
                        locale={locale}
                        state={state[filter.name]}
                        from={null}
                        to={null}
                        labels={labels}
                        onClick={(day) => handleDayClick(filter.name, day)}
                        onClickPredefined={(type) =>
                          handlePredfinedRangeClick(filter.name, type)
                        }
                      />
                    )}
                  </ExpandableText>
                </div>
              ))}
          </div>
          {renderSearchField()}
          {renderActiveFilters()}
        </ExpandableText>
      </div>
    );
  };

  const renderLargeLayout = () => {
    if (hideFilters) {
      return null;
    }

    // Count the visible filters
    const visibleFilters = filters.filter(
      (filter) => !filter.static && !filter.hidden
    );

    return (
      <div className={styles.main}>
        <div className={styles.input}>
          <div className={classNames(styles.wrapper)}>
            <div
              className={styles.filters}
              style={{
                gridTemplateColumns: `repeat(${
                  visibleFilters.length + 1
                }, auto)`,
              }}
            >
              {filters
                .filter((filter) => !filter.static && !filter.hidden)
                .map((filter) => {
                  // Get
                  return (
                    <React.Fragment key={filter.name}>
                      <button
                        data-name={filter.name}
                        key={filter.name}
                        className={classNames(styles.filter, {
                          [styles.filterOpen]: open === filter.name,
                        })}
                        onClick={() => {
                          toggleFilter(filter.name);
                        }}
                      >
                        <span className="plus-icon"></span>
                        {filter.label}
                      </button>
                      <div
                        className={classNames(
                          styles.options,
                          styles.filterOptions
                        )}
                      >
                        <ExpandableText
                          key={filter.name}
                          expanded={open === filter.name}
                          calcMargins={filter.type === 'date'}
                          shouldFadeIn={true}
                        >
                          {filter.type === 'options' && (
                            <OptionsType
                              items={filter.items}
                              active={state[filter.name]}
                              onClick={(value) =>
                                toggleOption(filter.name, value)
                              }
                            />
                          )}
                          {filter.type === 'date' && (
                            <DateType
                              locale={locale}
                              state={state[filter.name]}
                              from={null}
                              to={null}
                              labels={labels}
                              onClick={(day) =>
                                handleDayClick(filter.name, day)
                              }
                              onClickPredefined={(type) =>
                                handlePredfinedRangeClick(filter.name, type)
                              }
                            />
                          )}
                        </ExpandableText>
                      </div>
                    </React.Fragment>
                  );
                })}
            </div>
            {renderSearchField()}
          </div>
        </div>
        <div className={styles.options}>{renderActiveFilters()}</div>
      </div>
    );
  };

  useEffect(() => {
    if (page !== prevPage && prevPage) {
      debouncedApiCall(state);
    }
  }, [page]);

  useEffect(() => {
    const keys = Object.keys(state);

    if (!loadOnInit & (keys.length > 0) || loadOnInit) {
      if ((prevState && !isEqual(prevState, state)) || !prevState) {
        setPage(1);
        debouncedApiCall(state);
      }
    } else {
      if (result.data) {
        setResult({ loading: false, data: null });
      }
    }
  }, [state]);

  // Show the initial content of the results portal if
  // - We're not loading on Init
  // - We have no active filters
  // - We have active filters, but we have not data yet
  const keys = Object.keys(state);
  const showInitial =
    !loadOnInit && (keys.length === 0 || (keys.length >= 1 && !result.data));

  const useDefaultSettings = keys.length === 0 && defaultSettings;

  return (
    <div>
      {useLargeLayout ? renderLargeLayout() : renderSmallLayout()}
      <Result
        view={resultView}
        content={content}
        root={resultPortal}
        {...result}
        showInitial={showInitial}
        currentPage={page}
        onPaginate={setPage}
        labels={labels}
        itemWidth={itemWidth}
        defaultTitle={useDefaultSettings ? defaultSettings.title : null}
      />
    </div>
  );
}

/**
 * Declare expected prop types.
 *
 * @type {Object}
 */
Filter.propTypes = {
  content: PropTypes.string.isRequired,
  endpoint: PropTypes.string.isRequired,
  resultPortal: PropTypes.string,
  resultView: PropTypes.oneOf(['entries', 'studies', 'archive']),
  filters: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      name: PropTypes.string,
      type: PropTypes.oneOf(['date', 'options']),
      options: PropTypes.object,
      items: PropTypes.arrayOf(
        PropTypes.shape({
          name: PropTypes.string,
          value: PropTypes.string,
        })
      ),
    })
  ),
  query: PropTypes.string,
  parent: PropTypes.string,
  allowSearch: PropTypes.bool,
  hideFilters: PropTypes.bool,
  loadOnInit: PropTypes.bool,
  labels: PropTypes.shape({
    btnShowFilters: PropTypes.string,
    btnHideFilters: PropTypes.string,
    active: PropTypes.string,
    placeholder: PropTypes.string,
    rangeToday: PropTypes.string,
    rangeWeekend: PropTypes.string,
    rangeMonth: PropTypes.string,
    formLabel: PropTypes.string,
  }),
  itemWidth: PropTypes.oneOf([3, 4, 6]),
  locale: PropTypes.oneOf(['nb', 'en']),
  defaultSettings: PropTypes.shape({
    title: PropTypes.string,
    filters: PropTypes.object,
  }),
  csrf: PropTypes.shape({
    token: PropTypes.string,
    tokenName: PropTypes.string,
  }),
};

/**
 * Declare defaults for non-required props.
 *
 * @type {Object}
 */
Filter.defaultProps = {
  filters: [],
  resultPortal: null,
  resultView: 'entries',
  query: null,
  parent: null,
  allowSearch: true,
  hideFilters: false,
  labels: {
    btnShowFilters: 'Vis filter',
    btnHideFilters: 'Skjul filter',
    active: 'Valgte filter',
    placeholder: 'Søk',
    rangeToday: 'I dag',
    rangeWeekend: 'Neste helg',
    rangeMonth: 'Denne måneden',
    rangeUpcoming: 'Kommende',
    formLabel: null,
  },
  loadOnInit: false,
  itemWidth: 3,
  locale: 'nb',
  defaultSettings: null,
  csrf: {
    token: null,
    tokenName: null,
  },
};

export default Filter;
